import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Button, Icon, Modal, message } from 'antd'
import {
  editImageDeepAI,
  generateImageWithReferenceNanoBanana,
  getTaskStatus
} from '../../../../../../services/api/aiVideoGeneration'
import { createCreativeGuidelineSessionPayload } from '../../utils/payloadHelpers'
import ActiveReferencesBar from './ActiveReferencesBar'
import ConversationList from './ConversationList'
import ConfirmSaveModal from './ConfirmSaveModal'
import DrawingCanvas from './DrawingCanvas'
import PromptComposer from './PromptComposer'
import ReferencePanel from './ReferencePanel'
import {
  COLORS,
  EMPTY_REFERENCE_IMAGES,
  blurActiveElement,
  releaseModalFocus,
  wait
} from './utils'
import './ImageEditorModal.scss'

const POLL_INTERVAL = 5000
const MAX_POLL_ATTEMPTS = 60
const DEFAULT_INFERENCE_STEPS = 28
const DEFAULT_GUIDANCE_SCALE = 0.85

const makeMessageId = prefix =>
  `${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`

const getModeLabel = mode =>
  mode === 'kontext' ? 'Single Reference' : 'Multi-Reference'

const ImageEditorModalContent = ({
  visible,
  mode = 'kontext',
  onCancel,
  onConfirm,
  initialPrompt = '',
  referenceImages = EMPTY_REFERENCE_IMAGES,
  aspectRatio,
  currentImage = null,
  videoId,
  creativeDirection = null
}) => {
  const [currentEditingImage, setCurrentEditingImage] = useState(currentImage)
  const [selectedReferences, setSelectedReferences] = useState([])
  const [uploadedReferences, setUploadedReferences] = useState([])
  const [messages, setMessages] = useState([])
  const [selectedForSave, setSelectedForSave] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [showReferencePicker, setShowReferencePicker] = useState(false)
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  const [annotatingImageSrc, setAnnotatingImageSrc] = useState(null)

  const mountedRef = useRef(false)
  const generationIdRef = useRef(0)
  const sendingRef = useRef(false)
  const isGeneratingRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      sendingRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!visible) return

    generationIdRef.current += 1
    sendingRef.current = false
    setCurrentEditingImage(currentImage)
    setSelectedReferences([])
    setUploadedReferences([])
    setMessages([])
    setSelectedForSave(null)
    setIsGenerating(false)
    setShowReferencePicker(false)
    setShowConfirmSave(false)
    setAnnotatingImageSrc(null)
  }, [visible, mode, currentImage])

  useEffect(() => {
    isGeneratingRef.current = isGenerating
  }, [isGenerating])

  const sourceReferenceImages = useMemo(
    () =>
      Array.isArray(referenceImages) ? referenceImages : EMPTY_REFERENCE_IMAGES,
    [referenceImages]
  )

  const allReferences = useMemo(
    () => [...uploadedReferences, ...sourceReferenceImages],
    [sourceReferenceImages, uploadedReferences]
  )

  const activeReferenceUrls = useMemo(() => {
    const seen = new Set()

    return [currentEditingImage, ...(selectedReferences || [])].filter(url => {
      if (!url || seen.has(url)) return false

      seen.add(url)
      return true
    })
  }, [currentEditingImage, selectedReferences])

  const isCurrentGeneration = useCallback(
    generationId =>
      mountedRef.current && generationIdRef.current === generationId,
    []
  )

  const pollTask = useCallback(async taskId => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      const response = await getTaskStatus(taskId)
      const status = response?.data?.status

      if (status === 'success') return response?.data?.output
      if (status === 'failed' || status === 'error') {
        throw new Error(response?.data?.error || 'Task failed')
      }

      await wait(POLL_INTERVAL)
    }

    throw new Error('Timeout')
  }, [])

  const handleSend = useCallback(
    promptValue => {
      const promptText = String(promptValue || '').trim()

      if (sendingRef.current || isGeneratingRef.current) return false

      if (!promptText) {
        message.error('Please enter a prompt')
        return false
      }

      const generationId = generationIdRef.current + 1
      generationIdRef.current = generationId
      sendingRef.current = true

      const userMessageId = makeMessageId('user')
      const loadingMessageId = makeMessageId('loading')
      const referencesUsed = activeReferenceUrls

      setMessages(previousMessages => [
        ...previousMessages,
        {
          id: userMessageId,
          type: 'user',
          content: promptText,
          references: referencesUsed
        },
        {
          id: loadingMessageId,
          type: 'loading'
        }
      ])
      setIsGenerating(true)

      ;(async () => {
        try {
          const commonPayload = {
            prompt: promptText,
            ratio: aspectRatio,
            video_id: videoId,
            ...createCreativeGuidelineSessionPayload({ creativeDirection })
          }

          const response =
            mode === 'kontext'
              ? await editImageDeepAI({
                  ...commonPayload,
                  image_to_be_edited: currentEditingImage,
                  references: selectedReferences || [],
                  num_inference_steps: DEFAULT_INFERENCE_STEPS,
                  guidance_scale: DEFAULT_GUIDANCE_SCALE
                })
              : await generateImageWithReferenceNanoBanana({
                  ...commonPayload,
                  image_to_be_edited: currentEditingImage,
                  reference_images: selectedReferences || []
                })

          if (!isCurrentGeneration(generationId)) return

          const taskId = response?.data?.task_id
          if (!taskId) throw new Error('No task ID')

          const resultUrl = await pollTask(taskId)

          if (!isCurrentGeneration(generationId)) return

          setMessages(previousMessages =>
            previousMessages
              .filter(item => item.id !== loadingMessageId)
              .concat({
                id: makeMessageId('assistant'),
                type: 'assistant',
                content: resultUrl
              })
          )
          setSelectedForSave(resultUrl)
          setCurrentEditingImage(resultUrl)
          setSelectedReferences([])
        } catch (error) {
          if (!isCurrentGeneration(generationId)) return

          setMessages(previousMessages =>
            previousMessages
              .filter(item => item.id !== loadingMessageId)
              .concat({
                id: makeMessageId('error'),
                type: 'error',
                content: error.message || 'Generation failed'
              })
          )
        } finally {
          if (isCurrentGeneration(generationId)) {
            setIsGenerating(false)
            sendingRef.current = false
          }
        }
      })()

      return true
    },
    [
      aspectRatio,
      activeReferenceUrls,
      creativeDirection,
      currentEditingImage,
      isCurrentGeneration,
      mode,
      pollTask,
      selectedReferences,
      videoId
    ]
  )

  const handleUploadComplete = useCallback(url => {
    const uploadReference = {
      url,
      sceneTitle: 'Uploaded',
      shotTitle: 'Uploaded'
    }

    setUploadedReferences(previousReferences => [
      uploadReference,
      ...previousReferences
    ])
    setSelectedReferences(previousReferences =>
      previousReferences.includes(url)
        ? previousReferences
        : [...previousReferences, url]
    )
  }, [])

  const handleAnnotate = useCallback(url => {
    releaseModalFocus(() => setAnnotatingImageSrc(url))
  }, [])

  const handleAnnotateCurrent = useCallback(() => {
    if (currentEditingImage) handleAnnotate(currentEditingImage)
  }, [currentEditingImage, handleAnnotate])

  const handleDrawingDone = useCallback(
    annotatedUrl => {
      blurActiveElement()

      if (annotatingImageSrc === currentEditingImage) {
        setCurrentEditingImage(annotatedUrl)
      } else {
        const annotatedReference = {
          url: annotatedUrl,
          sceneTitle: 'Uploaded',
          shotTitle: 'Annotated'
        }

        setUploadedReferences(previousReferences => [
          annotatedReference,
          ...previousReferences
        ])
        setSelectedReferences(previousReferences => {
          const references = previousReferences.map(referenceUrl =>
            referenceUrl === annotatingImageSrc ? annotatedUrl : referenceUrl
          )

          if (!references.includes(annotatedUrl)) {
            references.push(annotatedUrl)
          }

          return references.filter(
            (referenceUrl, index, list) =>
              referenceUrl && list.indexOf(referenceUrl) === index
          )
        })
      }

      setAnnotatingImageSrc(null)
    },
    [annotatingImageSrc, currentEditingImage]
  )

  const handleSave = useCallback(() => {
    if (!selectedForSave) {
      message.error('Select a generated image first')
      return
    }

    releaseModalFocus(() => setShowConfirmSave(true))
  }, [selectedForSave])

  const handleUseImage = useCallback(url => {
    setSelectedForSave(url)
    setCurrentEditingImage(url)
  }, [])

  const handleConfirmSave = useCallback(() => {
    if (!selectedForSave) return

    releaseModalFocus(() => {
      onConfirm(selectedForSave)
      setShowConfirmSave(false)
    })
  }, [onConfirm, selectedForSave])

  const handleCancel = useCallback(() => {
    releaseModalFocus(onCancel)
  }, [onCancel])

  const handleOpenReferences = useCallback(() => {
    if (isGeneratingRef.current) return

    releaseModalFocus(() => setShowReferencePicker(true))
  }, [])

  const handleCloseReferences = useCallback(() => {
    releaseModalFocus(() => setShowReferencePicker(false))
  }, [])

  const handleApplyReferences = useCallback(nextReferences => {
    setSelectedReferences(Array.isArray(nextReferences) ? nextReferences : [])
    releaseModalFocus(() => setShowReferencePicker(false))
  }, [])

  const handleDismissConfirmSave = useCallback(() => {
    releaseModalFocus(() => setShowConfirmSave(false))
  }, [])

  return (
    <Modal
      visible={visible}
      title={null}
      centered
      onCancel={handleCancel}
      afterClose={blurActiveElement}
      closable={!isGenerating}
      width={1000}
      maskClosable={false}
      destroyOnClose
      bodyStyle={{
        padding: 0,
        height: '75vh',
        display: 'flex',
        flexDirection: 'column'
      }}
      footer={null}
    >
      <div
        style={{
          height: 58,
          padding: '0 20px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: COLORS.white
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: COLORS.text }}>
            Image Editor
            <span
              style={{
                marginLeft: 8,
                fontSize: 12,
                color: COLORS.textMuted,
                fontWeight: 400
              }}
            >
              ({getModeLabel(mode)})
            </span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            Edit, generate, then save the selected result
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            padding: '20px 24px',
            background: COLORS.white
          }}
        >
          <ConversationList
            messages={messages}
            currentImage={currentEditingImage}
            initialPrompt={initialPrompt}
            selectedForSave={selectedForSave}
            aspectRatio={aspectRatio}
            onAnnotateCurrent={handleAnnotateCurrent}
            onUseImage={handleUseImage}
          />

          <ActiveReferencesBar
            references={activeReferenceUrls}
            onAnnotate={handleAnnotate}
            onOpenReferences={handleOpenReferences}
            disabled={isGenerating}
          />

          <PromptComposer
            isGenerating={isGenerating}
            onSend={handleSend}
            videoId={videoId}
            aspectRatio={aspectRatio}
            onUploadComplete={handleUploadComplete}
          />
        </div>
      </div>

      <div
        style={{
          height: 58,
          padding: '0 24px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: COLORS.white
        }}
      >
        <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
          {selectedForSave ? (
            <>
              <Icon
                type="check-circle"
                theme="filled"
                style={{ color: COLORS.success, marginRight: 6 }}
              />
              Generated image selected
            </>
          ) : (
            'Generate or choose an image to save'
          )}
        </span>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button onClick={handleCancel} disabled={isGenerating}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!selectedForSave || isGenerating}
            style={{
              background: COLORS.primary,
              borderColor: COLORS.primary,
              fontWeight: 500
            }}
          >
            Save Image
          </Button>
        </div>
      </div>

      <ConfirmSaveModal
        visible={showConfirmSave}
        currentImage={currentImage}
        selectedImage={selectedForSave}
        onCancel={handleDismissConfirmSave}
        onConfirm={handleConfirmSave}
      />

      {showReferencePicker ? (
        <ReferencePanel
          visible={showReferencePicker}
          references={allReferences}
          currentImage={currentEditingImage}
          selectedReferences={selectedReferences}
          onApply={handleApplyReferences}
          onCancel={handleCloseReferences}
        />
      ) : null}

      {annotatingImageSrc ? (
        <DrawingCanvas
          visible
          imageSrc={annotatingImageSrc}
          onDone={handleDrawingDone}
          onCancel={() => releaseModalFocus(() => setAnnotatingImageSrc(null))}
          videoId={videoId}
        />
      ) : null}
    </Modal>
  )
}

const ImageEditorModal = memo(props => {
  if (!props.visible) return null

  return <ImageEditorModalContent {...props} />
})

export default ImageEditorModal
